<script setup>
import Logo from '@/Shared/Logo.vue'
import MainMenu from '@/Shared/MainMenu.vue'
import { Link } from '@inertiajs/vue3'
import FlashMessages from '@/Shared/FlashMessages.vue'
import Icon from '@/Shared/Icon.vue'
import Dropdown from '@/Shared/Dropdown.vue'
import { ref, provide, onMounted, onBeforeUnmount } from 'vue'

defineProps({
  auth: {
    required: true,
    type: Object
  }
})

// Ya no usamos la funcionalidad de colapsar el menú
const isMenuCollapsed = ref(false)

// Estado para controlar si el menú móvil está abierto
const isMobileMenuOpen = ref(false)

// Compartir el estado con MainMenu
provide('isMenuCollapsed', isMenuCollapsed)
provide('isMobileMenuOpen', isMobileMenuOpen)

// Función para abrir/cerrar el menú móvil
function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value

  if (isMobileMenuOpen.value) {
    // Prevenir scroll cuando el menú está abierto
    document.body.classList.add('overflow-hidden', 'md:overflow-auto')
  } else {
    document.body.classList.remove('overflow-hidden', 'md:overflow-auto')
  }
}

// Cerrar el menú móvil cuando se hace clic fuera de él
function handleClickOutside(event) {
  const mobileMenu = document.getElementById('mobile-menu')
  const mobileMenuButton = document.getElementById('mobile-menu-button')

  if (
    isMobileMenuOpen.value &&
    mobileMenu &&
    !mobileMenu.contains(event.target) &&
    !mobileMenuButton.contains(event.target)
  ) {
    toggleMobileMenu()
  }
}

// Cerrar el menú móvil cuando se cambia el tamaño de la ventana a desktop
function handleResize() {
  if (window.innerWidth >= 768 && isMobileMenuOpen.value) {
    isMobileMenuOpen.value = false
    document.body.classList.remove('overflow-hidden', 'md:overflow-auto')
  }
}

// Configurar y limpiar event listeners
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', handleResize)
})

// Proporcionar el estado al componente hijo (MainMenu)
provide('isMenuCollapsed', isMenuCollapsed)
</script>

<template>
  <div class="h-full leading-none">
    <div id="dropdown" />
    <div class="h-full md:flex md:flex-col">
      <div class="h-full md:flex md:flex-col md:h-screen bg-secondary-50">
        <!-- Barra de encabezado fija al estilo Metronic -->
        <div
          class="md:hidden fixed top-0 left-0 right-0 h-16 bg-indigo-900/95 shadow-md z-40 flex items-center justify-between px-4"
        >
          <div class="flex items-center gap-3">
            <button
              id="mobile-menu-button"
              class="p-2 rounded-lg bg-indigo-800 shadow-sm flex items-center justify-center"
              @click="toggleMobileMenu"
            >
              <Icon name="MenuHamburger" class="w-5 h-5 fill-white" />
            </button>

            <Link href="/dashboard" class="flex items-center">
              <Logo class="text-lg text-white" :minimal="true" />
            </Link>
          </div>

          <Dropdown placement="bottom-end">
            <template #default>
              <button
                class="p-2 rounded-lg bg-indigo-800 shadow-sm flex items-center justify-center"
              >
                <Icon name="User" class="w-5 h-5 text-white" />
              </button>
            </template>
            <template #dropdown>
              <div class="bg-white rounded-lg shadow-lg overflow-hidden w-64 ring-1 ring-gray-200">
                <!-- Cabecera del perfil -->
                <div class="p-4 flex items-center gap-3 border-b border-gray-100">
                  <div
                    class="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-lg font-medium text-white"
                  >
                    {{ auth.user.name ? auth.user.name.charAt(0).toUpperCase() : 'U' }}
                  </div>
                  <div class="overflow-hidden">
                    <div class="font-medium text-gray-800 truncate">{{ auth.user.name }}</div>
                    <div class="text-xs text-gray-500 truncate">{{ auth.user.email }}</div>
                  </div>
                </div>
                <!-- Opciones del perfil -->
                <div class="py-1">
                  <Link
                    href="/profile"
                    class="flex items-center px-4 py-2.5 hover:bg-gray-50 transition-colors duration-200 text-gray-700"
                  >
                    <Icon name="User" class="w-5 h-5 text-indigo-600 mr-3" />
                    <span>Mi Perfil</span>
                  </Link>
                  <div class="border-t border-gray-100 my-1"></div>
                  <Link
                    href="/logout"
                    method="post"
                    as="button"
                    class="flex items-center px-4 py-2.5 hover:bg-gray-50 transition-colors duration-200 w-full text-left text-gray-700"
                  >
                    <Icon name="ArrowLeftOnRectangle" class="w-5 h-5 text-indigo-600 mr-3" />
                    <span>Cerrar Sesión</span>
                  </Link>
                </div>
              </div>
            </template>
          </Dropdown>
        </div>

        <!-- Overlay oscuro cuando el menú móvil está abierto -->
        <div
          v-if="isMobileMenuOpen"
          class="md:hidden fixed inset-0 bg-gray-900/50 z-40 transition-opacity duration-300"
          :class="isMobileMenuOpen ? 'opacity-100' : 'opacity-0'"
        ></div>

        <!-- Menú móvil estilo GitLab (se desliza desde la izquierda) -->
        <div
          id="mobile-menu"
          class="md:hidden fixed inset-y-0 left-0 w-[280px] bg-indigo-900/95 z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto"
          :class="isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'"
        >
          <div class="p-4 border-b border-indigo-800/30 flex items-center justify-between">
            <Link href="/dashboard" @click="isMobileMenuOpen = false">
              <Logo class="text-xl text-white" :minimal="true" />
            </Link>
            <button
              class="p-1.5 rounded-md text-indigo-300 hover:text-white hover:bg-indigo-600 transition-colors duration-200"
              @click="toggleMobileMenu"
            >
              <Icon name="X" class="w-5 h-5" />
            </button>
          </div>
          <div class="py-2">
            <MainMenu />
          </div>
          <!-- Información de usuario y versión en móvil -->
          <div class="px-4 py-3 mt-auto border-t border-indigo-800/30 text-indigo-200 text-xs">
            <div class="flex items-center justify-between mb-2">
              <Link
                href="/changelog"
                class="text-indigo-200 hover:text-white transition-colors duration-200 flex items-center gap-1"
                @click="isMobileMenuOpen = false"
              >
                <Icon name="Information" class="w-3 h-3" />
                <span>v1.4</span>
              </Link>
            </div>
            <div class="flex items-center gap-2">
              <Icon name="User" class="w-3 h-3 text-secondary-300" />
              <span class="truncate">{{ auth.user.email }}</span>
            </div>
          </div>
        </div>
        <div class="h-full md:flex md:flex-grow md:overflow-hidden pt-16 md:pt-0 overflow-y-auto">
          <div
            class="hidden flex-shrink-0 bg-indigo-900 overflow-y-auto md:flex flex-col shadow-lg w-60"
          >
            <!-- Menú principal -->
            <div class="flex-grow">
              <MainMenu />
            </div>

            <!-- Información de usuario y versión -->
            <div class="px-3 py-3 border-t border-indigo-800/30">
              <div class="flex items-center justify-between text-indigo-200 text-xs mb-2">
                <Link
                  href="/changelog"
                  class="text-indigo-200 hover:text-white transition-colors duration-200 flex items-center gap-1"
                >
                  <Icon name="Information" class="w-3 h-3" />
                  <span>v1.4</span>
                </Link>
              </div>
              <Link href="/profile" class="flex items-center gap-2 text-indigo-200 text-xs">
                <Icon name="User" class="w-3 h-3 text-indigo-300" />
                <span class="truncate">{{ auth.user.email }}</span>
              </Link>
            </div>
          </div>
          <div class="px-4 py-8 md:flex-1 md:p-12 md:overflow-y-auto main-mobile mb-8 relative">
            <FlashMessages />

            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
